import type { CodeLanguage } from '@pebblecode/shared';

import type { Expr, Stmt } from './ir.js';
import { exprIds, stmtExprs } from './ir.js';

export type LineRange = [start: number, end: number];

/**
 * One view of a program: the code itself plus where each block ended up. `lineMap` maps a block
 * id to the 1-based [start, end] lines it produced (value blocks map to the line they appear on);
 * `headMap` maps a statement block to its header line(s) only — what lights up while it executes.
 * Typed code mode (ADR 0012) fills the same shape from the student's own text.
 */
export interface GeneratedCode {
  language: CodeLanguage;
  code: string;
  lineMap: Record<string, LineRange>;
  headMap: Record<string, LineRange>;
}

/**
 * Accumulates generated lines and records, for every block, the 1-based line range it produced.
 * `lineMap` covers a statement's whole span (header to closing brace) and maps value blocks to
 * the line they appear on; `headMap` covers only a statement's header, which is what lights up
 * while the statement (or its loop check) executes.
 */
export class CodeWriter {
  private readonly lines: string[] = [];
  readonly lineMap: Record<string, LineRange> = {};
  readonly headMap: Record<string, LineRange> = {};
  private depth = 0;

  constructor(private readonly indentUnit: string) {}

  get count(): number {
    return this.lines.length;
  }

  line(text: string): number {
    this.lines.push(text === '' ? '' : this.indentUnit.repeat(this.depth) + text);
    return this.lines.length;
  }

  indented(fn: () => void): void {
    this.depth++;
    try {
      fn();
    } finally {
      this.depth--;
    }
  }

  /** Map every block inside `e` to `line`, unless it is already mapped. */
  mapExpr(e: Expr, line: number): void {
    for (const id of exprIds(e)) this.lineMap[id] ??= [line, line];
  }

  /**
   * Emit a statement: `head` writes its header line(s), `rest` its body and closing lines.
   * Value blocks of the statement not mapped by `rest` are mapped to the header.
   */
  stmt(s: Stmt, head: () => void, rest?: () => void): void {
    const start = this.count + 1;
    head();
    const headEnd = this.count;
    rest?.();
    const end = this.count;
    if (end < start) return;
    this.lineMap[s.id] = [start, end];
    this.headMap[s.id] = [start, headEnd];
    for (const e of stmtExprs(s)) {
      for (const id of exprIds(e)) this.lineMap[id] ??= [start, headEnd];
    }
  }

  /** Map a block (e.g. "when program starts") to the whole program. */
  mapAll(id: string | null): void {
    if (id && this.count > 0) this.lineMap[id] = [1, this.count];
  }

  toString(): string {
    return this.lines.length === 0 ? '' : `${this.lines.join('\n')}\n`;
  }
}
